import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MoreHorizontal, Pencil, Trash2, EyeOff, Flag, BarChart3, ThumbsDown, Bookmark, BookmarkCheck } from "lucide-react";
import { useSavedPosts } from "@/hooks/useSavedPosts";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PostAnalyticsDialog from "./PostAnalyticsDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HIDDEN_KEY = "pdv-hidden-posts";

export function getHiddenPostIds() {
  try {
    return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) || "[]"));
  } catch { return new Set(); }
}

function saveHidden(setLike) {
  localStorage.setItem(HIDDEN_KEY, JSON.stringify(Array.from(setLike)));
}

export function hidePostLocally(postId) {
  const s = getHiddenPostIds();
  s.add(postId);
  saveHidden(s);
}

export function unhidePostLocally(postId) {
  const s = getHiddenPostIds();
  s.delete(postId);
  saveHidden(s);
}

const REASONS = [
  { key: "spam",       label: "Spam ou propaganda enganosa" },
  { key: "offensive",  label: "Conteúdo ofensivo" },
  { key: "violence",   label: "Violência ou ódio" },
  { key: "fraud",      label: "Fraude ou golpe" },
  { key: "other",      label: "Outro motivo" },
];

/**
 * Post options 3-dot menu.
 * Owner (or admin): Edit, Delete, Hide, Report
 * Others: Hide, Report
 * Admin sees "Excluir (moderação)" for any post that isn't theirs.
 */
export default function PostMenu({ post, user, onEdited, onDeleted, onHidden, onNotInterested }) {
  const isOwner = user?.user_id === post.user_id;
  const isAdmin = !!user?.is_admin;
  const [editOpen, setEditOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [editForm, setEditForm] = useState({ content: post.content || "", image_url: post.image_url || "", video_url: post.video_url || "" });
  const [reportReason, setReportReason] = useState("offensive");
  const [reportDetails, setReportDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const { isSaved, toggle: toggleSave } = useSavedPosts();
  const saved = isSaved(post.post_id);
  const doSave = async () => {
    try {
      const now = await toggleSave(post.post_id);
      toast.success(now ? "Publicação salva" : "Removida dos salvos");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/posts/${post.post_id}`, {
        content: editForm.content,
        image_url: editForm.image_url || null,
        video_url: editForm.video_url || null,
      });
      toast.success("Post atualizado!");
      setEditOpen(false);
      onEdited?.({ ...post, ...editForm });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  const doDelete = async () => {
    if (!window.confirm(isOwner ? "Excluir este post?" : "Excluir este post por conteúdo impróprio?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/posts/${post.post_id}`);
      toast.success("Post excluído");
      onDeleted?.(post.post_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  const doHide = () => {
    hidePostLocally(post.post_id);
    toast.success("Post ocultado do seu feed");
    onHidden?.(post.post_id);
  };

  const submitReport = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/posts/${post.post_id}/report`, { reason: reportReason, details: reportDetails });
      toast.success("Denúncia enviada. Nossa equipe irá analisar.");
      setReportOpen(false);
      setReportDetails("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`post-menu-btn-${post.post_id}`}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
            aria-label="Opções do post"
          >
            <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid={`post-menu-${post.post_id}`}>
          <DropdownMenuItem onClick={doSave} data-testid={`post-menu-save-${post.post_id}`}>
            {saved ? <BookmarkCheck className="w-4 h-4 mr-2 text-primary" /> : <Bookmark className="w-4 h-4 mr-2" />} {saved ? "Salvo · remover" : "Salvar publicação"}
          </DropdownMenuItem>
          {(isOwner || isAdmin) && (
            <DropdownMenuItem onClick={() => setAnalyticsOpen(true)} data-testid={`post-menu-analytics-${post.post_id}`}>
              <BarChart3 className="w-4 h-4 mr-2" /> Ver métricas
            </DropdownMenuItem>
          )}
          {isOwner && (
            <DropdownMenuItem onClick={() => setEditOpen(true)} data-testid={`post-menu-edit-${post.post_id}`}>
              <Pencil className="w-4 h-4 mr-2" /> Editar
            </DropdownMenuItem>
          )}
          {(isOwner || isAdmin) && (
            <DropdownMenuItem
              onClick={doDelete}
              className="text-destructive"
              data-testid={`post-menu-delete-${post.post_id}`}
            >
              <Trash2 className="w-4 h-4 mr-2" /> {isOwner ? "Excluir" : "Excluir (moderação)"}
            </DropdownMenuItem>
          )}
          {(isOwner || isAdmin) && <DropdownMenuSeparator />}
          <DropdownMenuItem onClick={doHide} data-testid={`post-menu-hide-${post.post_id}`}>
            <EyeOff className="w-4 h-4 mr-2" /> Ocultar
          </DropdownMenuItem>
          {!isOwner && onNotInterested && (
            <DropdownMenuItem onClick={() => onNotInterested(post)} data-testid={`post-menu-not-interested-${post.post_id}`}>
              <ThumbsDown className="w-4 h-4 mr-2" /> Ver menos disso
            </DropdownMenuItem>
          )}
          {!isOwner && (
            <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid={`post-menu-report-${post.post_id}`} className="text-destructive">
              <Flag className="w-4 h-4 mr-2" /> Denunciar
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid={`post-edit-dialog-${post.post_id}`}>
          <DialogHeader><DialogTitle>Editar publicação</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea
              data-testid={`post-edit-content-${post.post_id}`}
              rows={4}
              value={editForm.content}
              onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
            />
            <Input
              data-testid={`post-edit-image-${post.post_id}`}
              placeholder="URL da imagem (opcional)"
              value={editForm.image_url}
              onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
            />
            <Input
              data-testid={`post-edit-video-${post.post_id}`}
              placeholder="URL do vídeo (opcional)"
              value={editForm.video_url}
              onChange={(e) => setEditForm({ ...editForm, video_url: e.target.value })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-full">Cancelar</Button>
            <Button onClick={saveEdit} disabled={busy} className="rounded-full" data-testid={`post-edit-save-${post.post_id}`}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md" data-testid={`post-report-dialog-${post.post_id}`}>
          <DialogHeader><DialogTitle>Denunciar publicação</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Escolha o motivo. Sua denúncia é anônima para o autor.</p>
            <div className="space-y-1">
              {REASONS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setReportReason(r.key)}
                  data-testid={`post-report-reason-${r.key}-${post.post_id}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition-colors ${
                    reportReason === r.key
                      ? "border-destructive bg-destructive/5 text-destructive"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <Textarea
              data-testid={`post-report-details-${post.post_id}`}
              rows={3}
              placeholder="Detalhes (opcional)"
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} className="rounded-full">Cancelar</Button>
            <Button
              onClick={submitReport}
              disabled={busy}
              variant="destructive"
              className="rounded-full gap-1"
              data-testid={`post-report-submit-${post.post_id}`}
            >
              <Flag className="w-4 h-4" /> Enviar denúncia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PostAnalyticsDialog
        open={analyticsOpen}
        onOpenChange={setAnalyticsOpen}
        postId={post.post_id}
      />
    </>
  );
}
